import type {Flow_AbstractComponent} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useFocus,
  useRole,
  useInteractions,
  safePolygon,
  FloatingPortal,
  FloatingFocusManager,
} from '@spaced-out/ui-design-system/lib/floating-ui';

import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {
  spaceNone,
  spaceXXSmall,
} from '@spaced-out/ui-design-system/lib/styles/variables/_space';

import {classify} from 'src/utils/classify';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

import type {ButtonProps} from '@spaced-out/ui-design-system/lib/components/Button';
import type {
  MenuOption,
  MenuProps,
} from '@spaced-out/ui-design-system/lib/components/Menu';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {Menu} from '@spaced-out/ui-design-system/lib/components/Menu';
import {SideMenuLink} from '@spaced-out/ui-design-system/lib/components/SideMenuLink';
import type {SideMenuLinkProps} from '@spaced-out/ui-design-system/lib/components/SideMenuLink';

import css from './side-menu-dropdown.css';


export const ANCHOR_POSITION_TYPE = Object.freeze({
  top: 'top',
  topStart: 'top-start',
  topEnd: 'top-end',
  bottom: 'bottom',
  bottomStart: 'bottom-start',
  bottomEnd: 'bottom-end',
  right: 'right',
});

export const STRATEGY_TYPE = Object.freeze({
  absolute: 'absolute',
  fixed: 'fixed',
});

export type AnchorType =
  typeof ANCHOR_POSITION_TYPE[keyof typeof ANCHOR_POSITION_TYPE];
export type Strategy = typeof STRATEGY_TYPE[keyof typeof STRATEGY_TYPE];

type ClassNames = Readonly<{
  buttonWrapper?: string;
  dropdownContainer?: string;
  buttonIcon?: string;
}>;

export type SideMenuDropdownProps = ButtonProps & {
  classNames?: ClassNames;
  disabled?: boolean;
  menu?: MenuProps;
  positionStrategy?: Strategy;
  anchorPosition?: AnchorType;
  onOptionSelect?: (
    option: MenuOption,
    arg2?: React.SyntheticEvent<HTMLElement> | null | undefined,
  ) => unknown;
  sideMenuLink: SideMenuLinkProps;
  showStatusIndicator?: boolean;
  badgeTextDetails?: {
    [key: string]: string;
  };
};

// @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<HTMLDivElement & RefAttributes<SideMenuDropdownProps>>' is not assignable to type 'AbstractComponent<SideMenuDropdownProps, HTMLDivElement>'.
export const SideMenuDropdown: Flow_AbstractComponent<
  SideMenuDropdownProps,
  HTMLDivElement
> = React.forwardRef<SideMenuDropdownProps, HTMLDivElement>(
  // @ts-expect-error - TS2345 - Argument of type '({ anchorPosition, positionStrategy, size, onOptionSelect, menu, classNames, disabled, isFluid, sideMenuLink, showStatusIndicator, badgeTextDetails, }: SideMenuDropdownProps, forwardRef: ForwardedRef<SideMenuDropdownProps>) => Element' is not assignable to parameter of type 'ForwardRefRenderFunction<SideMenuDropdownProps, HTMLDivElement>'.
  (
    {
      anchorPosition = 'bottom-start',
      positionStrategy = STRATEGY_TYPE.fixed,
      size = 'medium',
      onOptionSelect,
      menu,
      classNames,
      disabled,
      isFluid,
      sideMenuLink,
      showStatusIndicator,
      badgeTextDetails = {},
      testId,
    }: SideMenuDropdownProps,
    forwardRef,
  ) => {
    const menuBtnRef = React.useRef(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const {selectedValue, pageNameKey} = sideMenuLink;

    // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'SideMenuDropdownProps'.
    React.useImperativeHandle(forwardRef, () => menuBtnRef.current);

    const {x, y, refs, strategy, context} = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      strategy: positionStrategy,
      placement: anchorPosition,
      whileElementsMounted: autoUpdate,
      middleware: [shift(), flip(), offset(parseInt(spaceXXSmall))],
    });

    const hover = useHover(context, {
      handleClose: safePolygon(),
    });
    const focus = useFocus(context, {visibleOnly: true});
    const role = useRole(context, {role: 'menu'});

    const {getReferenceProps, getFloatingProps} = useInteractions([
      hover,
      focus,
      role,
    ]);

    return (
      <div
        data-testid={generateTestId({base: 'side-menu-dropdown', slot: testId})}
        className={classify(
          css.customDropdownContainer,
          {
            [css.isFluid]: isFluid,
          },
          classNames?.dropdownContainer,
        )}
        ref={menuBtnRef}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        {...getReferenceProps()}
      >
        <SideMenuLink
          {...sideMenuLink}
          disabled={disabled}
          // @ts-expect-error - TS2769 - No overload matches this call.
          size={size}
          ref={refs.setReference}
          status={showStatusIndicator ? 'information' : undefined}
          tabIndex={-1}
          classNames={{
            wrapper: classify({
              [css.selectedDashboardLink]: selectedValue === pageNameKey,
            }),
          }}
          testId={`${testId}-side-menu-link`}
        />
        {isOpen && menu && (
          <FloatingPortal>
            <FloatingFocusManager context={context} modal={false}>
              <div
                ref={refs.setFloating}
                style={{
                  display: 'flex',
                  position: strategy,
                  top: y ?? spaceNone,
                  left: x ?? spaceNone,
                  ...(isFluid && {width: sizeFluid}),
                  zIndex: 1,
                }}
                {...getFloatingProps()}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Menu
                  {...menu}
                  onSelect={(option, e) => {
                    onOptionSelect && onOptionSelect(option, e);
                    setIsOpen(false);
                    // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'never'.
                    menuBtnRef.current?.focus(); // Return focus to button
                  }}
                  size={menu.size || size}
                  classNames={{
                    optionTextContainer: css.optionTextContainer,
                    wrapper: menu.classNames?.wrapper,
                  }}
                  resolveLabel={(item) => {
                    const badgeText = badgeTextDetails[item.key];
                    if (badgeText) {
                      return (
                        <div className={css.menuResolver}>
                          <div>{item.label}</div>
                          <Badge
                            fill="blueDark"
                            text={badgeText}
                            classNames={{wrapper: css.menuBadge}}
                          />
                        </div>
                      );
                    } else {
                      return item.label || '';
                    }
                  }}
                />
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </div>
    );
  },
);
